import React, { useEffect, useState } from "react";
import { RotateCcw, Trash2, Users, Building2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { relativeTime } from "@/lib/utils";
import { ConfirmDeleteDialog } from "@/components/SwipeDelete";

const FILTERS = [["all", "All"], ["user", "Users"], ["development", "Developments"]];
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
const plural = (n) => `${n} item${n === 1 ? "" : "s"}`;

function linkedSummary(counts = {}) {
  const parts = Object.entries(counts).filter(([k, v]) => v > 0 && k !== "users" && k !== (counts.users ? "" : "developments"));
  return parts.length ? parts.map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ") : "No linked records";
}

function TrashRow({ item, checked, onToggle, onRestore, onPurge, busy }) {
  const Icon = item.kind === "user" ? Users : Building2;
  const left = daysLeft(item.expires_at);
  return (
    <div className={`flex items-center gap-3 px-4 sm:px-5 py-3 border-t border-neutral-100 transition-colors ${checked ? "bg-propera-light/40" : ""}`} data-testid={`trash-row-${item.id}`}>
      <input type="checkbox" data-testid={`trash-select-${item.id}`} checked={checked} onChange={onToggle} className="w-4 h-4 accent-[#0E8C5A] cursor-pointer" aria-label="Select" />
      <div className="w-9 h-9 rounded-xl bg-neutral-100 text-neutral-600 flex items-center justify-center flex-shrink-0"><Icon size={16} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-neutral-900 truncate">{item.label}<span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-400">{item.role || item.kind}</span></p>
        <p className="text-xs text-neutral-500 truncate">{item.sublabel ? `${item.sublabel} · ` : ""}{linkedSummary(item.counts)}</p>
        <p className="text-[11px] text-neutral-400 mt-0.5">Deleted {relativeTime(item.deleted_at)}{item.deleted_by_name ? ` by ${item.deleted_by_name}` : ""}</p>
      </div>
      <span data-testid={`trash-days-${item.id}`} className={`hidden sm:inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${left <= 3 ? "bg-red-50 text-red-700" : "bg-neutral-100 text-neutral-600"}`}>{left} day{left === 1 ? "" : "s"} left</span>
      <button data-testid={`trash-restore-${item.id}`} disabled={busy} onClick={onRestore} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-propera-light text-propera border border-propera/30 hover:opacity-90 disabled:opacity-60"><RotateCcw size={12} /> Restore</button>
      <button data-testid={`trash-purge-${item.id}`} disabled={busy} onClick={onPurge} className="p-2 rounded-full text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Delete forever"><Trash2 size={15} /></button>
    </div>
  );
}

export default function AdminTrashTab() {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  const [selected, setSelected] = useState([]);
  const [purging, setPurging] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, [kind]);
  async function load() {
    const { data } = await api.get(`/admin/trash${kind === "all" ? "" : `?kind=${kind}`}`);
    setItems(data);
    setSelected((s) => s.filter((id) => data.some((i) => i.id === id)));
  }

  async function restore(ids) {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/trash/restore", { ids });
      toast.success(`Restored ${data.restored === 1 ? data.labels[0] : plural(data.restored)}`);
      data.errors?.forEach((e) => toast.error(e));
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function purge() {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/trash/purge", { ids: purging });
      toast.success(`Permanently deleted ${plural(data.purged)}`);
      setPurging(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (!items) return <p className="text-sm text-neutral-500">Loading…</p>;
  const all = items.length > 0 && items.every((i) => selected.includes(i.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <>
      <p className="text-sm text-neutral-500 -mt-4 mb-5">Deleted users and developments stay here for 30 days with everything linked to them, then are removed for good.</p>
      <div className="flex gap-2 flex-wrap items-center">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`trash-filter-${k}`} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${kind === k ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>{label}</button>
        ))}
        <span className="text-xs text-neutral-500" data-testid="trash-count">{items.length} in bin</span>
      </div>
      {selected.length > 0 && (
        <div className="sticky top-20 z-20 mt-4 flex items-center gap-3 flex-wrap rounded-2xl bg-neutral-900 text-white px-4 py-3 shadow-lg animate-fade-up" data-testid="trash-bulk-bar">
          <span className="text-sm font-semibold" data-testid="trash-selected-count">{selected.length} selected</span>
          <button data-testid="trash-bulk-restore" disabled={busy} onClick={() => restore(selected)} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#0E8C5A] hover:opacity-90"><RotateCcw size={13} /> Restore selected</button>
          <button data-testid="trash-bulk-purge" disabled={busy} onClick={() => setPurging(selected)} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-red-500 hover:bg-red-400"><Trash2 size={13} /> Delete forever</button>
          <button data-testid="trash-bulk-clear" onClick={() => setSelected([])} className="ml-auto text-xs text-white/70 hover:text-white">Clear</button>
        </div>
      )}
      <div className="soft-card mt-4 overflow-hidden" data-testid="trash-list">
        <div className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wider font-semibold">
          <input type="checkbox" data-testid="trash-select-all" disabled={!items.length} checked={all} onChange={() => setSelected(all ? [] : items.map((i) => i.id))} className="w-4 h-4 accent-[#0E8C5A] cursor-pointer" aria-label="Select all" />
          <span>Deleted item</span>
        </div>
        {items.map((it) => (
          <TrashRow key={it.id} item={it} busy={busy} checked={selected.includes(it.id)} onToggle={() => toggle(it.id)} onRestore={() => restore([it.id])} onPurge={() => setPurging([it.id])} />
        ))}
        {items.length === 0 && (
          <div className="p-10 text-center" data-testid="trash-empty">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-neutral-100 text-neutral-500 flex items-center justify-center mb-3"><Inbox size={20} /></div>
            <p className="text-sm text-neutral-500">Nothing has been deleted recently.</p>
          </div>
        )}
      </div>
      <ConfirmDeleteDialog
        open={!!purging}
        busy={busy}
        title={purging ? `Delete ${plural(purging.length)} forever?` : ""}
        description="These items and their linked records will be permanently removed. This can't be undone."
        confirmLabel="Delete forever"
        onCancel={() => setPurging(null)}
        onConfirm={purge}
      />
    </>
  );
}
